import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { transcriptUrl, type ConversationDetail, type TeamMember } from "../../api";
import { Avatar } from "../../components/Avatar";
import { BackIcon, CheckIcon, InfoIcon, MoreIcon } from "../../components/Icons";
import { Menu } from "../../components/Menu";
import { statusLabel, visitorLabel } from "../../lib/format";
import { useI18n } from "../../lib/i18n";

/**
 * The top of an open conversation: who it is, who has it, what it is about,
 * and the one action that matters most — marking it solved.
 */
export function ConversationHeader({
  detail,
  team,
  meId,
  onBack,
  onInfo,
  onAssign,
  onStatus,
  onTags,
  onAddTag,
  onTranscript,
  onTicket,
  isAdmin,
  onDelete,
}: {
  detail: ConversationDetail;
  team: TeamMember[];
  meId: number;
  /** Owners and admins only: deleting a conversation is irreversible. */
  isAdmin: boolean;
  onDelete: () => void;
  onBack: () => void;
  onInfo: () => void;
  onAssign: (operatorId: number | null) => void;
  onStatus: (status: "open" | "resolved") => void;
  onTags: (tags: string[]) => void;
  /** Asks for a tag in a dialog — the way to add one where the tag box does not fit. */
  onAddTag: () => void;
  onTranscript: () => void;
  /** Absent where tickets are not offered (the read-only history). */
  onTicket?: () => void;
}): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.conv;
  const { conversation, visitor, site } = detail;
  const [newTag, setNewTag] = useState("");
  const name = visitorLabel(
    { visitorName: visitor?.displayName ?? null, visitorEmail: visitor?.email ?? null, visitorId: conversation.visitorId },
    lang,
  );
  const assignee = team.find((m) => m.id === conversation.assignedOperatorId) ?? null;
  const done = conversation.status === "resolved" || conversation.status === "closed";

  function addTag(): void {
    const tag = newTag.trim();
    if (!tag || conversation.tags.includes(tag.toLocaleLowerCase(lang))) return;
    onTags([...conversation.tags, tag]);
    setNewTag("");
  }

  return (
    <header class="conv-head">
      <button class="btn ghost icon back" onClick={onBack} aria-label={t.back}>
        <BackIcon />
      </button>
      <div class="title">
        <h2>{name}</h2>
        <div class="sub">
          <span class={`chip status-${conversation.status}`}>{statusLabel(conversation.status, lang)}</span>
          {site ? <span class="hide-sm">{site.name}</span> : null}
          {conversation.ticketRef ? <span class="chip ticket">{t.ticketChip(conversation.ticketRef.id)}</span> : null}
          {conversation.tags.map((tag) => (
            <span key={tag} class="chip">
              {tag}
              <button
                aria-label={t.removeTag(tag)}
                title={t.removeTagTitle}
                onClick={() => onTags(conversation.tags.filter((x) => x !== tag))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </div>

      <div class="actions">
        <Menu
          label={assignee ? t.assignedChange(assignee.displayName) : t.assignSomeone}
          buttonClass="btn sm"
          button={
            assignee ? (
              <>
                <Avatar name={assignee.displayName} color={assignee.color} url={assignee.avatarUrl} size={20} />
                {assignee.id === meId ? t.mine : assignee.displayName}
              </>
            ) : (
              t.assign
            )
          }
        >
          {(close) => (
            <>
              {conversation.assignedOperatorId !== meId ? (
                <button
                  role="menuitem"
                  onClick={() => {
                    onAssign(meId);
                    close();
                  }}
                >
                  {t.assignMe}
                </button>
              ) : null}
              {team.length > 0 ? <hr /> : null}
              {team.map((member) => (
                <button
                  key={member.id}
                  role="menuitem"
                  onClick={() => {
                    onAssign(member.id);
                    close();
                  }}
                >
                  <Avatar name={member.displayName} color={member.color} url={member.avatarUrl} size={22} />
                  {member.displayName}
                  <span class={`presence ${member.presence}`} style={{ width: "8px", height: "8px", borderRadius: "50%" }} />
                  {member.id === conversation.assignedOperatorId ? (
                    <span class="check">
                      <CheckIcon size={16} />
                    </span>
                  ) : null}
                </button>
              ))}
              {conversation.assignedOperatorId !== null ? (
                <>
                  <hr />
                  <button
                    role="menuitem"
                    onClick={() => {
                      onAssign(null);
                      close();
                    }}
                  >
                    {t.unassign}
                  </button>
                </>
              ) : null}
            </>
          )}
        </Menu>

        <Menu label={t.tagAdd} button={t.tag} wrapClass="hide-sm">
          {(close) => (
            <form
              style={{ padding: "6px" }}
              onSubmit={(e) => {
                e.preventDefault();
                addTag();
                close();
              }}
            >
              <input
                class="tag-input"
                placeholder={t.tagPlaceholder}
                maxLength={40}
                value={newTag}
                onInput={(e) => setNewTag(e.currentTarget.value)}
                autoFocus
              />
            </form>
          )}
        </Menu>

        {done ? (
          <button class="btn sm" onClick={() => onStatus("open")}>
            {t.reopen}
          </button>
        ) : (
          <button class="btn sm primary" onClick={() => onStatus("resolved")}>
            <CheckIcon size={16} />
            {t.resolve}
          </button>
        )}

        <Menu label={t.more} buttonClass="btn ghost icon" button={<MoreIcon />}>
          {(close) => (
            <>
              <button
                role="menuitem"
                disabled={!visitor?.email}
                title={visitor?.email ? undefined : t.noEmail}
                onClick={() => {
                  onTranscript();
                  close();
                }}
              >
                {t.transcript}
              </button>
              {/* A plain link: the browser downloads it with the session
                  cookie, and nothing of the file passes through this script. */}
              <a role="menuitem" class="menu-link" href={transcriptUrl(conversation.id)} download onClick={close}>
                {t.downloadTranscript}
              </a>
              {/* The tag box is hidden on a phone for want of room; this is
                  the same action where there is some. */}
              <button
                role="menuitem"
                class="show-sm"
                onClick={() => {
                  onAddTag();
                  close();
                }}
              >
                {t.tagAdd}
              </button>
              {onTicket && !conversation.ticketRef ? (
                <button
                  role="menuitem"
                  disabled={!site?.ticketsEnabled || !visitor?.identityVerified}
                  title={
                    !site?.ticketsEnabled ? t.ticketNoWebhook : !visitor?.identityVerified ? t.ticketNoIdentity : undefined
                  }
                  onClick={() => {
                    onTicket();
                    close();
                  }}
                >
                  {t.openTicket}
                </button>
              ) : null}
              <hr />
              {/* Last in the menu and visibly separate: nothing here is undoable,
                  and the click itself still has to get past a confirmation. */}
              <button
                role="menuitem"
                class="danger"
                disabled={!isAdmin}
                title={isAdmin ? undefined : t.deleteAdminOnly}
                onClick={() => {
                  onDelete();
                  close();
                }}
              >
                {t.delete}
              </button>
            </>
          )}
        </Menu>

        <button class="btn ghost icon info-toggle" onClick={onInfo} aria-label={t.info} title={t.info}>
          <InfoIcon />
        </button>
      </div>
    </header>
  );
}
